import {
  transformUrnToWitboostId,
  WitboostDomain,
  WitboostSystem,
} from '@agilelab/plugin-wb-builder-common';
import {
  AsyncEnumFilter,
  createNameWithVersionMap,
  GenericElement,
  NotFoundException,
  Pagination,
  SearchFilter,
  TableCellProps,
  WbResponsiveTableToolbar,
} from '@agilelab/plugin-wb-platform';
import { practiceShaperApiRef } from '@agilelab/plugin-wb-practice-shaper';
import { PART_OF } from '@agilelab/plugin-wb-practice-shaper-common';
import { parseEntityRef, stringifyEntityRef } from '@backstage/catalog-model';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { CatalogApi, catalogApiRef } from '@backstage/plugin-catalog-react';
import { Box, Typography } from '@material-ui/core';
import { FC, ReactNode, useCallback, useMemo, useState } from 'react';
import { useAsyncFn } from 'react-use';
import useAsync from 'react-use/lib/useAsync';
import { contextProviderFactory } from '../scopeProvider.factory';
import {
  FilterProps,
  MyProvider,
  ProviderFilters,
  ScopeProviderType,
  SystemFilters,
} from '../types';
import { getLabelAndValue } from '../../utils';

export type SystemTypeEntity = GenericElement & WitboostSystem;

interface SystemContextType
  extends ScopeProviderType<GenericElement, ProviderFilters> {}

const [useSystemContext, SystemContextProvider] =
  contextProviderFactory<SystemContextType>();

export default useSystemContext;

export interface SystemProviderProps {
  children: ReactNode;
}

const DomainFilter: FC<FilterProps> = ({
  value,
  changeFilters,
  getOptions,
}) => {
  return (
    <AsyncEnumFilter<{ name: string; ref: string }>
      field="Domain"
      value={value ?? undefined}
      onChange={domain => {
        changeFilters('domain', domain);
      }}
      renderOption={o => o.name}
      renderValue={v => v.name}
      onSearch={(searchValue, options) =>
        options.filter(option =>
          new RegExp(searchValue, 'ig').test(option.name),
        )
      }
      getOptions={getOptions}
    />
  );
};

const TypeFilter: FC<FilterProps> = ({ value, changeFilters, getOptions }) => {
  return (
    <AsyncEnumFilter<{ name: string; ref: string }>
      field="Type"
      value={value}
      onChange={type => {
        changeFilters('type', type);
      }}
      renderOption={o => o.name}
      renderValue={v => v.name}
      onSearch={(searchValue, options) =>
        options.filter(option =>
          new RegExp(searchValue, 'ig').test(option.name),
        )
      }
      getOptions={getOptions}
    />
  );
};

export const SystemFiltersSection = () => {
  const { filters, changeFilters, resetFilters } = useSystemContext();
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);
  const practiceShaperApi = useApi(practiceShaperApiRef);

  const [_, fetchAvailableResourceTypes] = useAsyncFn(async (): Promise<
    { name: string; ref: string }[]
  > => {
    const { resourceTypes } = await practiceShaperApi.getResourceTypes(
      {},
      await identityApi.getCredentials(),
    );
    return resourceTypes.map(rt => ({ name: rt.displayName, ref: rt.name }));
  }, []);

  const fetchAvailableDomains = useCallback(async () => {
    const response = await catalogApi.getEntities({
      filter: { kind: 'domain' },
    });
    return response.items.map(item => {
      const ref = stringifyEntityRef({
        kind: 'domain',
        name: item.metadata.name,
        namespace: item.metadata.namespace,
      });
      const domain = item as WitboostDomain;
      return { name: domain.spec.mesh.name, ref };
    });
  }, [catalogApi]);

  return (
    <WbResponsiveTableToolbar
      onClear={resetFilters}
      style={{ marginTop: '16px' }}
      marginBottom
    >
      <WbResponsiveTableToolbar.Filters>
        <SearchFilter
          key="search"
          value={filters.search ?? ''}
          changeFilters={changeFilters}
        />

        <DomainFilter
          key="domain"
          value={filters.domain ?? []}
          changeFilters={changeFilters}
          getOptions={fetchAvailableDomains}
        />
        <TypeFilter
          key="type"
          value={filters.type ?? []}
          changeFilters={changeFilters}
          getOptions={fetchAvailableResourceTypes}
        />
      </WbResponsiveTableToolbar.Filters>
    </WbResponsiveTableToolbar>
  );
};

export const SystemProvider: FC<SystemProviderProps> = props => {
  const { children } = props;
  const catalogApi = useApi(catalogApiRef);
  const [filters, setFilters] = useState<SystemFilters>({});
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [prevCursor, setPrevCursor] = useState<string | undefined>(undefined);
  const [count, setCount] = useState<number>(0);
  const [pagination, setPagination] = useState<Pagination>({
    limit: 25,
    currentPage: 0,
  });
  const defaultColumns: TableCellProps<GenericElement>[] = useMemo(() => {
    return [
      {
        headerName: 'Name',
        field: 'name',
        fieldRender: (field: GenericElement) => {
          return (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography>{field.displayName}</Typography>
            </Box>
          );
        },
      },
      {
        headerName: 'Description',
        field: 'description',
        fieldRender: (field: GenericElement) => {
          return (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography>{field.description}</Typography>
            </Box>
          );
        },
      },
    ];
  }, []);

  const systemState = useAsync(async () => {
    const result = await catalogApi.queryEntities({
      filter: {
        kind: ['System'],
        [`relations.${PART_OF}`]: filters.domain?.map(d => d.ref) || [],
        'spec.type': filters.type?.map(d => d.ref) || [],
      },
      limit: pagination.limit,
      cursor: pagination.cursor,
      fullTextFilter: {
        term: filters.search!,
        fields: ['spec.mesh.name', 'metadata.name'],
      },
    });
    setNextCursor(result.pageInfo.nextCursor);
    setPrevCursor(result.pageInfo.prevCursor);
    setCount(result.totalItems);

    const adaptedValues = result.items.map(entity => getLabelAndValue(entity));

    const nameWithVersionMap = createNameWithVersionMap(
      adaptedValues,
      'displayName',
    );

    return adaptedValues.map(val => {
      return {
        ...val,
        displayName: nameWithVersionMap[val.id].nameWithVersion,
      };
    });
  }, [filters, pagination.limit, pagination.cursor]);

  const getRowId = (row: any) => row.id;

  return (
    <SystemContextProvider
      value={
        {
          entitiesState: systemState,
          columns: defaultColumns,
          getRowId,
          filters,
          changeFilters: <K extends keyof SystemFilters>(
            key: K,
            filterValue: SystemFilters[K],
          ) => {
            setPagination(p => ({ ...p, currentPage: 0, cursor: undefined }));
            setFilters(f => ({ ...f, [key]: filterValue }));
          },
          resetFilters: () => {
            setFilters({});
          },
          pagination,
          setPagination,
          count,
          nextCursor,
          prevCursor,
        } as unknown as ScopeProviderType<GenericElement, ProviderFilters>
      }
    >
      {children}
    </SystemContextProvider>
  );
};

export class SystemHandlerProvider
  implements MyProvider<GenericElement, ProviderFilters>
{
  private readonly catalogApi: CatalogApi;

  constructor(catalogApi: CatalogApi) {
    this.catalogApi = catalogApi;
  }

  useContext = useSystemContext;

  renderContextProvider(children: ReactNode) {
    return <SystemProvider>{children}</SystemProvider>;
  }

  filtersSection = () => <SystemFiltersSection />;

  async resolve(ref: string) {
    const resolvedRef = transformUrnToWitboostId(ref) ?? ref;
    const parsedEntityRef = parseEntityRef(resolvedRef);
    try {
      if (parsedEntityRef.kind === 'system') {
        const entity = await this.catalogApi.getEntityByRef(resolvedRef);
        if (entity) return getLabelAndValue(entity);
        throw new NotFoundException(
          `Cannot find a System entity with ref: ${ref}`,
        );
      }

      return undefined;
    } catch (error) {
      throw new Error(`Error while resolving entity with ref: ${ref}`);
    }
  }
}
